import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Check,
  Info,
  Loader2,
  Mail,
  MapPin,
  Phone,
  User,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

type SignupStatus = "pending" | "approved" | "declined";

interface Signup {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  location: string | null;
  relationship_to_community: string;
  referred_by: string | null;
  status: SignupStatus;
  approved_by: string | null;
  approved_at: string | null;
  declined_by: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  created_at: string;
  updated_at: string;
}

export default function AdminMatchmakerSignupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [signup, setSignup] = useState<Signup | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);

  const [isApproving, setIsApproving] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const [isDeclining, setIsDeclining] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const listPath = "/admin/matchmaker-signups";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) {
        setErrorState("Missing signup id.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setErrorState(null);

        const { data, error } = await supabase
          .from("matchmaker_signups")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (error) throw error;
        if (!data) {
          if (!cancelled) {
            setErrorState("Signup not found.");
            setSignup(null);
          }
          return;
        }
        if (!cancelled) setSignup(data as Signup);
      } catch (err: any) {
        console.error("matchmaker_signup_detail.load_failed");
        if (!cancelled) {
          setErrorState(err.message || "Failed to load signup.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleApprove = async () => {
    if (!id || isApproving) return;
    setIsApproving(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "approve-matchmaker-signup",
        { body: { signup_id: id } },
      );
      if (error) throw error;
      if (data && data.ok === false) {
        throw new Error(data.error || "Failed to approve signup");
      }

      if (data?.warning) {
        toast({
          title: "Approved, but email failed",
          description: `${data.warning}${
            data.magic_link ? `\nManual link: ${data.magic_link}` : ""
          }`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Application approved",
          description: "Welcome email sent.",
        });
      }
      setApproveDialogOpen(false);
      navigate(listPath);
    } catch (err: any) {
      console.error("matchmaker_signup.approve_failed");
      toast({
        title: "Error",
        description: err.message || "Failed to approve signup.",
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!id || isDeclining) return;
    setIsDeclining(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "decline-matchmaker-signup",
        { body: { signup_id: id, reason: declineReason.trim() || undefined } },
      );
      if (error) throw error;
      if (data && data.ok === false) {
        throw new Error(data.error || "Failed to decline signup");
      }

      toast({ title: "Application declined" });
      setDeclineDialogOpen(false);
      navigate(listPath);
    } catch (err: any) {
      console.error("matchmaker_signup.decline_failed");
      toast({
        title: "Error",
        description: err.message || "Failed to decline signup.",
        variant: "destructive",
      });
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <AdminLayout title="Matchmaker Signup">
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <Link
            to={listPath}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to signups
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : errorState ? (
          <Alert variant="destructive">
            <Info className="h-4 w-4" />
            <AlertTitle>Cannot review signup</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{errorState}</p>
              <Button variant="outline" onClick={() => navigate(listPath)}>
                Back to list
              </Button>
            </AlertDescription>
          </Alert>
        ) : signup ? (
          <div className="space-y-6">
            {/* Header */}
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-semibold text-primary">
                  {signup.full_name?.[0]?.toUpperCase() ?? "?"}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-2xl font-bold">{signup.full_name}</h3>
                    <StatusBadge status={signup.status} />
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Submitted{" "}
                    {format(new Date(signup.created_at), "PPpp")}
                  </p>
                </div>
              </div>
            </Card>

            {/* Status banner */}
            {signup.status === "approved" && (
              <Alert>
                <Check className="h-4 w-4" />
                <AlertTitle>Approved</AlertTitle>
                <AlertDescription>
                  Approved{" "}
                  {signup.approved_at &&
                    format(new Date(signup.approved_at), "PPpp")}
                  {signup.approved_by ? ` by admin ${signup.approved_by}` : ""}.
                  {signup.user_id && (
                    <span className="block mt-1 text-xs">
                      Linked auth user: <code>{signup.user_id}</code>
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {signup.status === "declined" && (
              <Alert variant="destructive">
                <X className="h-4 w-4" />
                <AlertTitle>Declined</AlertTitle>
                <AlertDescription>
                  Declined{" "}
                  {signup.declined_at &&
                    format(new Date(signup.declined_at), "PPpp")}
                  {signup.declined_by ? ` by admin ${signup.declined_by}` : ""}.
                  {signup.decline_reason && (
                    <span className="block mt-1">
                      Reason: {signup.decline_reason}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Contact */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">Contact</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{signup.email}</span>
                </div>
                {signup.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{signup.phone}</span>
                  </div>
                )}
                {signup.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{signup.location}</span>
                  </div>
                )}
                {signup.referred_by && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Referred by: {signup.referred_by}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Why */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">
                Relationship to community
              </h4>
              <p className="text-sm whitespace-pre-wrap text-foreground/90">
                {signup.relationship_to_community}
              </p>
            </Card>

            {signup.status === "pending" && (
              <Card className="p-6 sticky bottom-4 bg-background border-2">
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setDeclineDialogOpen(true)}
                    disabled={isApproving || isDeclining}
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Decline
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => setApproveDialogOpen(true)}
                    disabled={isApproving || isDeclining}
                  >
                    {isApproving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="mr-2 h-4 w-4" />
                    )}
                    Approve
                  </Button>
                </div>
              </Card>
            )}
          </div>
        ) : null}
      </div>

      {/* Approve confirm */}
      <AlertDialog
        open={approveDialogOpen}
        onOpenChange={(open) => {
          if (!isApproving) setApproveDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve this matchmaker?</AlertDialogTitle>
            <AlertDialogDescription>
              This will grant them the <strong>matchmaker</strong> role and
              email them a magic link to sign in and set a password. They'll
              have full access to the matchmaker dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isApproving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleApprove();
              }}
              disabled={isApproving}
            >
              {isApproving ? "Approving..." : "Approve"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Decline dialog */}
      <AlertDialog
        open={declineDialogOpen}
        onOpenChange={(open) => {
          if (!isDeclining) setDeclineDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline this application?</AlertDialogTitle>
            <AlertDialogDescription>
              The applicant will not be granted matchmaker access. You can
              optionally record a reason for the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="decline_reason">Reason (optional)</Label>
            <Textarea
              id="decline_reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={3}
              placeholder="Not enough community involvement, couldn't verify referral, etc."
              disabled={isDeclining}
            />
          </div>
          <Separator />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeclining}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeclineConfirm();
              }}
              disabled={isDeclining}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeclining ? "Declining..." : "Decline application"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function StatusBadge({ status }: { status: SignupStatus }) {
  if (status === "approved") return <Badge>Approved</Badge>;
  if (status === "declined") return <Badge variant="outline">Declined</Badge>;
  return <Badge variant="secondary">Pending</Badge>;
}
